import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Icon } from "@/components/Icon";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBetSlip } from "@/context/BetSlipContext";
import { formatOdds, useOddsFormat } from "@/lib/odds";
import { classifyLeg, workingOdds } from "@/lib/slipOdds";
import { usePoller, reconnectingChipProps } from "@/lib/usePoller";
import PlayWalletSelector from "@/components/ui/PlayWalletSelector";

const fmt = (n) => (Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const MARKET_UP = { moneyline: "Moneyline", spread: "Spread", total: "Total", prop: "Prop" };

// "Accept odds changes" preference — persisted so it survives opening/closing the slip (the mobile
// sheet remounts SlipBody each open). Conservative default OFF. Price-only moves auto-accept when ON;
// LINE changes never do.
const ACCEPT_ODDS_KEY = "pnr_accept_odds_changes";
const readAcceptOdds = () => { try { return localStorage.getItem(ACCEPT_ODDS_KEY) === "1"; } catch { return false; } };
const writeAcceptOdds = (v) => { try { localStorage.setItem(ACCEPT_ODDS_KEY, v ? "1" : "0"); } catch { /* ignore */ } };

export function SlipBody({ onClose }) {
  const { legs, remove, clear, setOdds, freeBet, setFreeBet } = useBetSlip();
  const { user, refresh } = useAuth();
  const oddsFmt = useOddsFormat();
  const [stake, setStake] = useState("");
  const [currency, setCurrency] = useState("coins"); // default: Coins
  const [mode, setMode] = useState("parlay"); // "parlay" | "singles" (only meaningful with 2+ legs)
  const [acceptChanges, setAcceptChangesState] = useState(readAcceptOdds);
  const toggleAccept = () => setAcceptChangesState((v) => { const nv = !v; writeAcceptOdds(nv); return nv; });
  const [placing, setPlacing] = useState(false);
  const [confirmOdds, setConfirmOdds] = useState(null);
  const [quotes, setQuotes] = useState({}); // `${eventId}:${outcomeId}` -> authoritative quote state

  const freeBets = user?.free_bets || [];
  const activeFB = freeBets.find((f) => f.id === freeBet) || null;

  // live-refresh authoritative PNR quote state every 1s. The poller (usePoller) guards against
  // overlapping requests and tracks freshness; on a stall we keep the last-good quotes on screen but
  // surface a subtle "reconnecting…" chip (never loosens any placement gate — the server is still the
  // sole authority on every /bets and /parlays call).
  const quoteKey = legs.map((l) => `${l.eventId}:${l.outcomeId}`).join(",");
  const { stale: quotesStale } = usePoller(async () => {
    if (legs.length === 0) return;
    const { data } = await api.post("/odds/quote", { legs: legs.map((l) => ({ event_id: l.eventId, outcome_id: l.outcomeId })) });
    const m = {};
    data.forEach((q) => {
      m[`${q.event_id}:${q.outcome_id}`] = {
        odds: q.odds,
        available: q.available,
        reason: q.reason,
        marketVersion: q.market_version,
        line: q.line,
        label: q.label,
        side: q.side,
        market: q.market,
        receivedAt: q.received_at,
        providerUpdatedAt: q.provider_updated_at,
        expiresAt: q.expires_at,
      };
    });
    setQuotes(m);
  }, { interval: 1000, staleAfter: 6000, enabled: legs.length > 0, deps: [quoteKey] });

  const qkey = (l) => `${l.eventId}:${l.outcomeId}`;
  // Classify every leg against its authoritative current quote (shared pure logic in lib/slipOdds).
  const states = {};
  legs.forEach((l) => { states[qkey(l)] = classifyLeg(l, quotes[qkey(l)]); });
  const moved = legs.filter((l) => states[qkey(l)].priceOnly);
  const lineMoved = legs.filter((l) => states[qkey(l)].lineChanged);
  const unavailable = legs.filter((l) => states[qkey(l)].unavail);
  const wOdds = (l) => workingOdds(l, quotes[qkey(l)], acceptChanges);
  // Pull the current authoritative quote into the given legs (explicit Accept / line-change Update).
  const applyQuotes = (list) => {
    const u = {};
    list.forEach((l) => {
      const q = quotes[qkey(l)];
      if (!q) return;
      u[qkey(l)] = {
        odds: q.odds, marketVersion: q.marketVersion, line: q.line, label: q.label,
        side: q.side, market: q.market, receivedAt: q.receivedAt,
        providerUpdatedAt: q.providerUpdatedAt, expiresAt: q.expiresAt,
      };
    });
    setOdds(u);
    setConfirmOdds(null);
  };
  const acceptNew = () => applyQuotes(moved);

  const multi = legs.length >= 2;
  const effMode = multi ? mode : "singles"; // 1 leg is always a single
  const isParlay = multi && effMode === "parlay";
  const combined = legs.reduce((a, l) => a * wOdds(l), 1);

  const bal = currency === "coins" ? (user?.coins || 0) : (user?.cash || 0);
  const curLabel = currency === "coins" ? "PCoins" : "PCash";
  const stakeNum = stake ? parseFloat(stake) : 0;
  const effStake = activeFB ? activeFB.amount : stakeNum;

  // payout: parlay = stake × combined; singles = Σ stake × each leg (stake per leg)
  let totalReturn = 0;
  if (effStake) {
    if (activeFB) totalReturn = effStake * Math.max(0, combined - 1); // free bet = profit only, treated as parlay/single
    else if (isParlay) totalReturn = effStake * combined;
    else totalReturn = legs.reduce((a, l) => a + effStake * wOdds(l), 0); // singles: stake each (working price)
  }
  const profit = Math.max(0, totalReturn - (activeFB ? 0 : effStake * (isParlay ? 1 : legs.length)));
  const displayMult = isParlay ? combined : (legs.length === 1 ? (wOdds(legs[0]) || 0) : null);

  const setQuick = (delta) => setStake((s) => String(Math.round(((parseFloat(s) || 0) + delta) * 100) / 100));
  // Max = the biggest PLACEABLE per-stake. Coins are whole-unit; cash keeps cents (no silently
  // discarded PCash). In singles mode every leg is staked SEPARATELY, so the per-bet Max is the
  // balance divided by the leg count (otherwise "Max" sets an unplaceable total of bal×legs).
  const setMax = () => {
    const perStakeDivisor = (multi && !isParlay) ? legs.length : 1;
    const perMax = bal / perStakeDivisor;
    const v = currency === "cash" ? Math.floor(perMax * 100) / 100 : Math.floor(perMax);
    setStake(String(v > 0 ? v : 0));
  };

  const place = async (acceptChange = false) => {
    if (legs.length === 0) return;
    // A changed LINE is a different proposition — it must be explicitly re-selected, never placed as-is.
    if (lineMoved.length > 0) { toast.error("A line changed — review the updated pick before placing."); return; }
    const amt = activeFB ? activeFB.amount : parseFloat(stake);
    if (!activeFB) {
      if (!amt || amt <= 0) { toast.error("Enter a valid stake"); return; }
      const needed = amt * (isParlay ? 1 : legs.length); // singles stake each leg
      if (needed > bal) { toast.error(`Insufficient ${curLabel} balance`); return; }
    }
    const accept = acceptChange || acceptChanges;
    // When auto-accepting, value each price-only-moved leg at its CURRENT authoritative quote so the
    // ticket records the exact price/line/version being booked (never a stale accepted price).
    const resolve = (l) => {
      const s = states[qkey(l)];
      if (accept && s && s.priceOnly && s.q && s.q.odds != null && !s.lineChanged) {
        return { ...l, odds: s.q.odds, marketVersion: s.q.marketVersion, line: s.q.line };
      }
      return l;
    };
    const rlegs = legs.map(resolve);
    setPlacing(true);
    try {
      if (isParlay) {
        await api.post("/parlays", {
          legs: rlegs.map((l) => ({
            event_id: l.eventId,
            outcome_id: l.outcomeId,
            accepted_odds: l.odds,
            accepted_line: l.line ?? null,
            accepted_version: l.marketVersion ?? null,
          })),
          stake: amt,
          currency: activeFB ? "cash" : currency,
          free_bet_id: activeFB?.id || null,
          accept_odds_change: accept,
          accepted_odds: Math.round(rlegs.reduce((a, l) => a * (Number(l.odds) || 1), 1) * 100) / 100,
        });
        toast.success(activeFB ? "Free bet parlay placed!" : "Parlay placed!");
      } else if (legs.length === 1 || activeFB) {
        const l = rlegs[0];
        await api.post("/bets", { event_id: l.eventId, outcome_id: l.outcomeId, stake: amt, currency: activeFB ? "cash" : currency,
          accepted_odds: confirmOdds ?? l.odds, accepted_line: l.line ?? null,
          accepted_version: l.marketVersion ?? null, accept_odds_change: accept, free_bet_id: activeFB?.id || null });
        toast.success(activeFB ? "Free bet placed!" : "Bet placed!");
      } else {
        // Singles mode with multiple legs — each leg is its own single bet (reuses /bets).
        let ok = 0, fail = 0;
        for (const l of rlegs) {
          try {
            await api.post("/bets", { event_id: l.eventId, outcome_id: l.outcomeId, stake: amt, currency,
              accepted_odds: l.odds, accepted_line: l.line ?? null,
              accepted_version: l.marketVersion ?? null, accept_odds_change: accept });
            ok += 1;
          } catch { fail += 1; }
        }
        if (ok && !fail) toast.success(`${ok} single bets placed!`);
        else if (ok && fail) toast.message(`${ok} placed · ${fail} could not be placed (check balance / odds).`);
        else { toast.error("Could not place bets — check balance and prices."); setPlacing(false); return; }
      }
      clear(); setStake(""); setConfirmOdds(null);
      await refresh();
      onClose && onClose();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.code === "line_changed") {
        // A different proposition — never auto-confirm. The poll-driven "line changed" banner lets the
        // customer explicitly Update (re-select the new line) or remove the pick.
        setConfirmOdds(null);
        toast.message(d.message || "A line changed — review the updated pick before placing.");
      } else if (e.response?.status === 409 && (d?.code === "odds_changed" || d?.code === "quote_refresh_required")) {
        const l = d?.event_id && d?.outcome_id
          ? legs.find((leg) => leg.eventId === d.event_id && String(leg.outcomeId) === String(d.outcome_id))
          : legs[0];
        if (l && d?.current_odds != null) {
          setOdds({
            [qkey(l)]: {
              odds: d.current_odds,
              marketVersion: d.current_version,
              line: d.current_line,
              label: d.current_label,
              side: d.current_side,
              market: d.current_market,
              receivedAt: d.received_at,
              providerUpdatedAt: d.provider_updated_at,
              expiresAt: d.expires_at,
            },
          });
          setConfirmOdds(d.current_odds);
        } else {
          setConfirmOdds(null);
        }
        toast.message(d.message || "Market refreshed — confirm the current price");
      } else {
        setConfirmOdds(null);
        toast.error(apiError(d) || d?.message || e.message);
      }
    } finally { setPlacing(false); }
  };

  const placeLabel = placing ? "Placing…"
    : confirmOdds != null ? "Confirm new price"
    : activeFB ? "Place free bet"
    : isParlay ? "Place parlay"
    : legs.length > 1 ? `Place ${legs.length} bets`
    : "Place bet";

  return (
    <div>
      {/* 1. header — Bet Slip | My Bets tabs (the play-wallet choice lives in the action area,
             as the deliberate "selecting" control — PREMIUM_UI_DIRECTION §4.3/§4.9) */}
      <div className="mb-3 flex items-center gap-3">
        <span className="flex items-center gap-1.5 whitespace-nowrap font-display text-[16px] font-extrabold tracking-tight text-white">
          Bet Slip
          {legs.length > 0 && <span className="flex h-[22px] min-w-[22px] items-center justify-center rounded-full bg-pnr-green px-1.5 font-display text-[12px] font-extrabold text-surface-1" data-testid="slip-count">{legs.length}</span>}
        </span>
        <Link to="/my-bets" onClick={onClose} className="whitespace-nowrap rounded-md text-[16px] font-semibold text-[#71717a] transition-colors hover:text-white focus-ring">My Bets</Link>
        {legs.length > 0 && quotesStale && (
          <span {...reconnectingChipProps()} title="Live prices may be delayed — we'll keep the last price shown until we reconnect.">
            <span className="h-1.5 w-1.5 rounded-full bg-[#F59E0B]" style={{ animation: "pnrPulse 1.6s ease-out infinite" }} />
            Reconnecting…
          </span>
        )}
      </div>

      {legs.length === 0 ? <EmptyBody /> : (<>
      {/* 2. Straight | Parlay switch (only with 2+ legs) */}
      {multi && !activeFB && (
        <div className="mb-3 inline-flex w-full rounded-lg p-0.5" style={{ background: "#141417", boxShadow: "inset 0 0 0 1px #26262B" }}>
          <button data-testid="slip-mode-singles" onClick={() => setMode("singles")}
            className="flex h-[42px] flex-1 items-center justify-center rounded-[9px] px-3 text-[14px] font-bold transition-colors lg:h-9" style={effMode === "singles" ? { background: "#1F1F23", color: "#fff", boxShadow: "inset 0 1px 0 rgba(255,255,255,.07)" } : { color: "#71717a" }}>
            Straight
          </button>
          <button data-testid="slip-mode-parlay" onClick={() => setMode("parlay")}
            className="flex h-[42px] flex-1 items-center justify-center gap-1.5 rounded-[9px] px-3 text-[14px] font-bold transition-colors lg:h-9" style={effMode === "parlay" ? { background: "#1F1F23", color: "#fff", boxShadow: "inset 0 1px 0 rgba(255,255,255,.07)" } : { color: "#71717a" }}>
            Parlay <span className="text-[11px] font-semibold text-[#71717a]">{legs.length}</span>
          </button>
        </div>
      )}

      {/* 3. legs card */}
      <div className="mb-2 max-h-[38vh] overflow-y-auto rounded-2xl" style={{ background: "#101013", boxShadow: "inset 0 0 0 1px #1F1F23" }} data-testid="slip-legs">
        {legs.map((l, idx) => {
          const s = states[qkey(l)];
          const q = s?.q;
          const isLineChanged = s?.lineChanged;
          const isMoved = s?.priceOnly && q && q.odds != null && Math.abs(q.odds - l.odds) > 1e-9;
          const up = isMoved && q.odds > l.odds;
          const isUnavail = s?.unavail;
          return (
            <div key={`${l.eventId}:${l.outcomeId}`} className="p-3.5" style={{ borderTop: idx > 0 ? "1px solid #1A1A1E" : "none", background: isUnavail ? "rgba(239,68,68,.05)" : "transparent" }} data-testid={`slip-leg-${l.eventId}-${l.outcomeId}`}>
              <div className="mb-1 flex items-center justify-between gap-2">
                <span className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.12em] text-[#71717a]">
                  {l.live && <span className="h-1.5 w-1.5 rounded-full bg-[#EF4444]" style={{ animation: "pnrPulse 1.6s ease-out infinite" }} />}
                  {[MARKET_UP[l.market] || "Pick", l.league].filter(Boolean).join(" · ")}
                </span>
                <button data-testid={`slip-remove-${l.eventId}-${l.outcomeId}`} aria-label="Remove pick" onClick={() => { remove(l.eventId, l.outcomeId); setConfirmOdds(null); }} className="-mr-1 flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-md text-[#52525b] transition-colors hover:text-white focus-ring"><Icon name="x" size={15} /></button>
              </div>
              <div className="flex items-start justify-between gap-2">
                <p className={`min-w-0 truncate text-[15px] font-bold ${isUnavail ? "text-[#71717a]" : "text-white"}`}>{l.label}</p>
                <span className="flex shrink-0 items-center gap-1.5 font-display text-[16px] font-extrabold tabular-nums">
                  {isMoved && <span className="text-[12px] font-semibold text-[#52525b] line-through">{formatOdds(l.odds, oddsFmt)}</span>}
                  <span className={isMoved ? (up ? "text-[#00D632]" : "text-[#EF4444]") : "text-white"} data-testid={isMoved ? `slip-leg-moved-${l.eventId}-${l.outcomeId}` : undefined}>
                    {formatOdds(isMoved ? q.odds : l.odds, oddsFmt)}
                  </span>
                </span>
              </div>
              {l.eventLabel && <p className="mt-0.5 truncate text-[12px] text-[#71717a]">{l.eventLabel}</p>}
              {isUnavail && <span className="mt-1.5 inline-flex rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#EF4444]" style={{ background: "rgba(239,68,68,.12)" }}>Unavailable</span>}
              {isLineChanged && !isUnavail && (
                <span className="mt-1.5 inline-flex rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#EF4444]" style={{ background: "rgba(239,68,68,.12)" }} data-testid={`slip-leg-linechanged-${l.eventId}-${l.outcomeId}`}>Line changed → {l.market === "total" ? "" : ""}{q?.line != null ? q.line : "new line"}</span>
              )}
            </div>
          );
        })}
        {isParlay && (
          <div className="flex items-center justify-between px-3.5 py-2.5" style={{ borderTop: "1px solid #1A1A1E", background: "#0F0F12" }}>
            <span className="text-xs text-[#71717a]">{legs.length}-leg parlay · Combined</span>
            <span className="rounded-lg px-2 py-0.5 font-display text-[14px] font-extrabold tabular-nums text-[#00D632]" style={{ background: "rgba(0,214,50,.12)", boxShadow: "inset 0 0 0 1px rgba(0,214,50,.35)" }} data-testid="slip-combined">{formatOdds(combined, oddsFmt)}</span>
          </div>
        )}
      </div>

      <div className="mb-3 flex justify-end">
        <button data-testid="slip-clear-all" onClick={clear} className="rounded-md px-1 text-[11px] font-semibold text-[#71717a] transition-colors hover:text-white focus-ring">Clear all</button>
      </div>

      {/* 4. price-moved banner — manual Accept when auto-accept is OFF; a note when it's ON (the current
             price is already reflected in the payout and will be booked on placement). */}
      {moved.length > 0 && !acceptChanges && (
        <div className="mb-2 flex items-center justify-between gap-2 rounded-lg px-3 py-2" style={{ background: "rgba(0,214,50,.08)", boxShadow: "inset 0 0 0 1px rgba(0,214,50,.35)" }}>
          <span className="text-[12px] font-semibold text-white">Price moved on {moved.length} pick{moved.length > 1 ? "s" : ""}</span>
          <button onClick={acceptNew} data-testid="slip-accept-new" className="shrink-0 rounded-md px-3 py-1 text-[12px] font-bold text-[#0A0A0A]" style={{ background: "#FAFAFA" }}>Accept</button>
        </div>
      )}
      {moved.length > 0 && acceptChanges && (
        <p className="mb-2 rounded-lg px-3 py-2 text-[12px] font-semibold text-[#00D632]" style={{ background: "rgba(0,214,50,.08)", boxShadow: "inset 0 0 0 1px rgba(0,214,50,.35)" }} data-testid="slip-auto-accepting">Auto-accepting the current price on {moved.length} pick{moved.length > 1 ? "s" : ""}.</p>
      )}
      {/* 4b. line-changed banner — a DIFFERENT proposition; always needs an explicit Update (re-select the
             new line) regardless of the auto-accept toggle. Placement is blocked until resolved. */}
      {lineMoved.length > 0 && (
        <div className="mb-2 flex items-center justify-between gap-2 rounded-lg px-3 py-2" style={{ background: "rgba(239,68,68,.10)", boxShadow: "inset 0 0 0 1px rgba(239,68,68,.35)" }} data-testid="slip-line-changed">
          <span className="min-w-0 text-[12px] font-semibold text-white">Line changed on {lineMoved.length} pick{lineMoved.length > 1 ? "s" : ""} — review before placing</span>
          <button onClick={() => applyQuotes(lineMoved)} data-testid="slip-update-line" className="shrink-0 rounded-md px-3 py-1 text-[12px] font-bold text-[#0A0A0A]" style={{ background: "#FAFAFA" }}>Update</button>
        </div>
      )}
      {/* 5. unavailable banner */}
      {unavailable.length > 0 && (
        <p className="mb-2 rounded-lg px-3 py-2 text-[12px] font-semibold text-[#EF4444]" style={{ background: "rgba(239,68,68,.12)", boxShadow: "inset 0 0 0 1px rgba(239,68,68,.32)" }} data-testid="slip-unavailable">{unavailable.length} pick{unavailable.length > 1 ? "s are" : " is"} no longer available — remove {unavailable.length > 1 ? "them" : "it"} to continue.</p>
      )}

      {freeBets.length > 0 && (
        <div className="mb-3" data-testid="slip-free-bets">
          <p className="mb-1.5 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-[#a1a1aa]"><Icon name="promotions" size={14} className="text-[#00D632]" /> Free bets</p>
          <div className="flex flex-wrap gap-2">
            {freeBets.map((f) => (
              <button key={f.id} data-testid={`slip-free-bet-${f.id}`} onClick={() => { setFreeBet(freeBet === f.id ? null : f.id); setConfirmOdds(null); }}
                className={`rounded-full border px-3 py-1.5 text-xs font-bold transition-colors ${freeBet === f.id ? "border-[#00D632] bg-[#00D632] text-[#0A0A0A]" : "border-[#00D632]/40 bg-[#00D632]/10 text-[#00D632] hover:bg-[#00D632]/20"}`}>
                ${Number(f.amount).toFixed(0)} Free Bet
              </button>
            ))}
          </div>
          {activeFB && (
            <p className="mt-1.5 text-[11px] text-[#a1a1aa]" data-testid="slip-free-bet-note">Free bet applied — you win the <span className="font-semibold text-[#00D632]">profit only</span> in PCash; your stake is not returned.</p>
          )}
        </div>
      )}
      {confirmOdds != null && (
        <p className="mb-2 rounded bg-white/5 px-2 py-1 text-[11px] font-semibold text-[#00D632]" data-testid="slip-odds-changed">Odds moved to {formatOdds(confirmOdds, oddsFmt)} — tap Confirm.</p>
      )}

      {/* 6b. play-wallet selector — the deliberate "selecting" control (§4.3). Drives the SAME
             currency state the slip already used ("coins" | "cash"). Hidden for a free bet (it
             forces PCash). */}
      {!activeFB && (
        <div className="mb-3" data-testid="slip-wallet">
          <PlayWalletSelector
            value={currency}
            onChange={setCurrency}
            coins={user?.coins || 0}
            cash={user?.cash || 0}
            showBonus={false}
            caption={false}
            size="sm"
            testidPrefix="slip-cur"
          />
        </div>
      )}

      {/* 7. stake */}
      {!activeFB ? (
        <div className="mb-3">
          <div className="mb-1.5 flex items-center justify-between text-[10px] font-extrabold uppercase tracking-[0.14em] text-[#71717a]">
            <span>{multi && !isParlay ? "Stake per bet" : "Stake"}</span>
            <span>Balance <span className="tabular-nums text-meta">{fmt(bal)}</span></span>
          </div>
          <div className="flex h-14 items-center gap-2 rounded-[14px] px-3.5 lg:h-[54px]" style={{ background: "#0A0A0A", boxShadow: "inset 0 0 0 1.5px #00D632, 0 0 0 4px rgba(0,214,50,.08)" }}>
            <Icon name={currency === "cash" ? "pnrcash" : "pnrcoin"} size={16} className="text-pnr-green" />
            <input data-testid="slip-stake" name="stake" type="number" value={stake} onChange={(e) => setStake(e.target.value)} placeholder="0"
              className="w-full bg-transparent font-display text-[22px] font-extrabold tabular-nums text-white outline-none placeholder:text-[#3f3f46]" />
            <span className="shrink-0 text-[11px] font-bold text-[#71717a]">{curLabel}</span>
          </div>
          <div className="mt-2 grid grid-cols-4 gap-1.5">
            {[["+100", () => setQuick(100)], ["+500", () => setQuick(500)], ["+1K", () => setQuick(1000)], ["Max", setMax]].map(([lbl, fn]) => (
              <button key={lbl} data-testid={`slip-quick-${lbl}`} onClick={fn}
                className="flex h-11 items-center justify-center rounded-[10px] font-display text-[13px] font-bold text-meta transition-colors hover:text-white focus-ring lg:h-9" style={{ background: "#141417", boxShadow: "inset 0 0 0 1px #26262B" }}>{lbl}</button>
            ))}
          </div>
        </div>
      ) : (
        <div className="mb-3 flex justify-between rounded-xl px-3.5 py-3 text-sm" style={{ background: "rgba(0,214,50,.1)", boxShadow: "inset 0 0 0 1px rgba(0,214,50,.4)" }} data-testid="slip-free-bet-stake">
          <span className="text-[#a1a1aa]">Free bet stake</span>
          <span className="font-display font-extrabold tabular-nums text-[#00D632]">{fmt(activeFB.amount)}</span>
        </div>
      )}

      {/* 8. summary — three unambiguous money lines (§1.4): TOTAL STAKE (leaves balance) vs
             POTENTIAL RETURN (stake + winnings) vs PROFIT (winnings only). */}
      <div className="mb-3 rounded-xl p-3.5" style={{ background: "#101013", boxShadow: "inset 0 0 0 1px #1F1F23" }}>
        {displayMult != null && (
          <div className="flex justify-between text-[13px]"><span className="text-meta">{isParlay ? "Parlay odds" : "Odds"}</span><span className="font-display font-bold tabular-nums text-white">{formatOdds(displayMult, oddsFmt)}</span></div>
        )}
        {!activeFB && (
          <div className="mt-1 flex justify-between text-[13px]">
            <span className="text-meta">{legs.length > 1 && !isParlay ? `Total stake · ${legs.length} bets` : "Total stake"}</span>
            <span className="flex items-center gap-1 font-semibold tabular-nums text-white" data-testid="slip-total-stake"><Icon name={currency === "cash" ? "pnrcash" : "pnrcoin"} size={13} className="text-meta" />{fmt(stakeNum * (isParlay ? 1 : legs.length))}</span>
          </div>
        )}
        {!activeFB && profit > 0 && (
          <div className="mt-1 flex justify-between text-[13px]"><span className="text-meta">Profit</span><span className="font-semibold tabular-nums text-pnr-green" data-testid="slip-profit">{fmt(profit)}</span></div>
        )}
        <div className="mt-2.5 flex items-end justify-between border-t border-surface-border pt-2.5">
          <p className="text-[13px] text-meta-strong">{activeFB ? "To win" : "Potential return"}</p>
          <span className="flex shrink-0 items-center gap-1.5">
            <Icon name={currency === "cash" ? "pnrcash" : "pnrcoin"} size={18} className="text-pnr-green" />
            <span className="font-display text-[26px] font-extrabold leading-none tabular-nums text-pnr-green" data-testid="slip-payout">{fmt(totalReturn)}</span>
          </span>
        </div>
      </div>

      {/* 9. accept odds changes — applies to singles, straights, parlays and SGP legs. Price-only moves
             auto-accept in either direction when ON; LINE changes always need an explicit Update. */}
      <button onClick={toggleAccept} data-testid="slip-accept-changes" role="switch" aria-checked={acceptChanges}
        className="mb-3 flex min-h-[48px] w-full items-center justify-between gap-3 rounded-xl px-3.5 py-2.5 text-left focus-ring" style={{ background: "#101013", boxShadow: "inset 0 0 0 1px #1F1F23" }}>
        <span className="min-w-0">
          <span className="block text-[13px] font-semibold text-white">Accept odds changes</span>
          <span className="block text-[11px] leading-tight text-[#71717a]">Automatically accept updated prices</span>
        </span>
        <span className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${acceptChanges ? "bg-pnr-green" : "bg-surface-border"}`}>
          <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${acceptChanges ? "left-[22px]" : "left-0.5"}`} />
        </span>
      </button>

      {/* 10. place — the one green primary CTA, lifted into the thumb zone with --elev-4 (§4.9) */}
      <button onClick={() => place(confirmOdds != null)} disabled={placing || unavailable.length > 0 || lineMoved.length > 0} data-testid="slip-place-button"
        className="flex h-[56px] w-full items-center justify-center gap-2 rounded-[15px] font-display text-[15px] font-extrabold transition-colors focus-ring active:scale-[0.99]"
        style={(placing || unavailable.length > 0 || lineMoved.length > 0) ? { background: "#141417", color: "#52525b" } : { background: "#00D632", color: "#0A0A0A", boxShadow: "var(--elev-4)" }}>
        {placeLabel}
        {!placing && confirmOdds == null && !activeFB && stakeNum > 0 && (
          // Show the TOTAL amount that will leave the balance. In singles mode each leg is staked
          // separately, so the real debit is stake × legs (not the per-pick figure).
          <span className="text-[13px] font-bold" style={{ color: "rgba(10,10,10,.7)" }}>· {fmt(stakeNum * (isParlay ? 1 : legs.length))} {curLabel}</span>
        )}
      </button>
      {/* 11. footer */}
      <p className="mt-2.5 flex items-center justify-center gap-1.5 text-center text-[10px] text-[#52525b]"><Icon name="shield" size={12} /> Odds lock when placed · settles automatically</p>
      </>)}
    </div>
  );
}

// Empty-slip body (the header stays above it) — SBX-06 EMPTY frame.
export function EmptyBody() {
  return (
    <div className="py-8 text-center" data-testid="slip-empty">
      <span className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-[20px]" style={{ background: "#141417", boxShadow: "inset 0 0 0 1px #26262B, inset 0 1px 0 rgba(255,255,255,.05)" }}><Icon name="mybets" size={28} className="text-[#52525b]" /></span>
      <p className="font-display text-[18px] font-extrabold text-white">Your slip is empty</p>
      <p className="mx-auto mt-1.5 max-w-[240px] text-xs leading-relaxed text-[#71717a]">Tap any price to add a pick. Two or more picks can go in a parlay.</p>
      <Link to="/sportsbook?view=live" className="mx-auto mt-4 inline-flex h-11 items-center rounded-xl px-4 text-[13px] font-bold text-white transition-colors hover:border-border-strong focus-ring" style={{ boxShadow: "inset 0 0 0 1px #26262B" }}>Browse live games</Link>
      <p className="mt-5 text-[11px] uppercase tracking-[0.14em] text-[#52525b]">18+ · No purchase necessary · Void where prohibited</p>
    </div>
  );
}

export function GlobalBetSlip() {
  const { count, legs } = useBetSlip();
  const oddsFmt = useOddsFormat();
  const parlayDecimal = (legs || []).reduce((a, l) => a * (Number(l.odds) || 1), 1);
  const location = useLocation();
  const [openMobile, setOpenMobile] = useState(false);
  const [openDesktop, setOpenDesktop] = useState(false);
  const mobileSheetRef = useRef(null);
  const mobileTriggerRef = useRef(null);
  const closeMobile = useCallback(() => setOpenMobile(false), []);

  // Mobile sheet a11y (UIB-07): Escape to dismiss, focus moves into the sheet on open and returns to
  // the trigger on close, and a simple focus trap keeps Tab inside the dialog while it's open.
  useEffect(() => {
    if (!openMobile) return;
    const sheet = mobileSheetRef.current;
    const prevFocus = document.activeElement;
    const focusables = () => Array.from(
      sheet?.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])') || []
    ).filter((el) => !el.disabled && el.offsetParent !== null);
    const first = focusables()[0] || sheet;
    first && first.focus && first.focus();
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); closeMobile(); return; }
      if (e.key === "Tab") {
        const f = focusables();
        if (!f.length) return;
        const firstEl = f[0], lastEl = f[f.length - 1];
        if (e.shiftKey && document.activeElement === firstEl) { e.preventDefault(); lastEl.focus(); }
        else if (!e.shiftKey && document.activeElement === lastEl) { e.preventDefault(); firstEl.focus(); }
      }
    };
    document.addEventListener("keydown", onKey);
    const trigger = mobileTriggerRef.current;
    return () => {
      document.removeEventListener("keydown", onKey);
      const back = trigger || prevFocus;
      back && back.focus && back.focus();
    };
  }, [openMobile, closeMobile]);
  // The slip only appears once you have a pick — on EVERY page, including the sportsbook
  // (per Ev: no empty slip box on desktop). On sportsbook/event it's then a right rail;
  // elsewhere a floating button + popover. Those pages drop their reserved 360px rail
  // gutter when the slip is empty (same count check) so there's no dead space.
  const railRoute = location.pathname.startsWith("/sportsbook") || location.pathname.startsWith("/event");
  if (count === 0) return null;
  return (
    <>
      {/* desktop: persistent right rail on sportsbook/event (content reserves space) */}
      {railRoute && (
        <div className="pointer-events-none fixed right-4 top-24 z-40 hidden w-[336px] lg:block">
          <div className="surface-premium-rail pointer-events-auto max-h-[calc(100vh-7rem)] overflow-y-auto rounded-2xl border border-[#1F1F23] p-4 text-white shadow-[var(--elev-4)]" data-testid="global-bet-slip">
            <SlipBody />
          </div>
        </div>
      )}
      {/* desktop: floating button + popover on every other page (never overlaps content) */}
      {!railRoute && count > 0 && (
        <div className="hidden lg:block">
          <button onClick={() => setOpenDesktop((v) => !v)} data-testid="slip-fab-desktop"
            className="fixed bottom-6 right-6 z-40 flex items-center gap-2 rounded-full bg-pnr-green px-5 py-3 font-display font-bold text-surface-1 transition-transform hover:scale-[1.03] focus-ring" style={{ boxShadow: "var(--elev-4)" }}>
            <Icon name="mybets" size={18} /> Bet Slip ({count})
          </button>
          <AnimatePresence>
            {openDesktop && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setOpenDesktop(false)} />
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 12 }}
                  className="surface-premium-rail fixed bottom-24 right-6 z-50 max-h-[75vh] w-[336px] overflow-y-auto rounded-2xl border border-[#1F1F23] p-4 text-white shadow-[var(--elev-4)]" data-testid="global-bet-slip">
                  <SlipBody onClose={() => setOpenDesktop(false)} />
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </div>
      )}
      {/* mobile floating button + sheet — only when picks exist */}
      {count > 0 && (
      <>
      {/* MobileBetSlipBar — sits ABOVE the 76px TabBar, never overlapping it (bug #6). */}
      <button ref={mobileTriggerRef} onClick={() => setOpenMobile(true)} data-testid="slip-bar"
        aria-haspopup="dialog" aria-expanded={openMobile}
        className="fixed left-1/2 z-40 flex h-[60px] w-[calc(100%-24px)] max-w-[520px] -translate-x-1/2 items-center gap-3 rounded-[18px] px-3 text-left focus-ring lg:hidden"
        style={{ bottom: "calc(76px + 8px + env(safe-area-inset-bottom))", background: "#18181C", boxShadow: "inset 0 0 0 1px #2F2F35, var(--elev-4)" }}>
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-pnr-green font-display text-xs font-extrabold text-surface-1">{count}</span>
        <span className="min-w-0 flex-1 leading-tight">
          <span className="block truncate text-sm font-bold text-white">{count} selection{count > 1 ? "s" : ""}</span>
          {count > 1 && <span className="block text-xs font-bold text-pnr-green">Parlay {formatOdds(parlayDecimal, oddsFmt)}</span>}
        </span>
        <span className="flex h-[40px] shrink-0 items-center rounded-xl bg-pnr-green px-4 font-bold text-surface-1">View Bet Slip</span>
      </button>
      <AnimatePresence>
        {openMobile && (
          // Mobile (<640): bottom sheet. Tablet (640–1023): right-hand 400px drawer (§3/§8).
          <motion.div className="fixed inset-0 z-50 flex items-end sm:items-stretch sm:justify-end lg:hidden" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="absolute inset-0 backdrop-blur-sm" style={{ background: "hsl(var(--overlay-scrim))" }} onClick={() => setOpenMobile(false)} />
            <motion.div ref={mobileSheetRef} role="dialog" aria-modal="true" aria-label="Bet slip" initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", damping: 28, stiffness: 300 }}
              className="surface-premium-rail relative max-h-[88vh] w-full overflow-y-auto rounded-t-[28px] border-t border-surface-border p-4 text-white sm:h-full sm:max-h-full sm:w-[400px] sm:rounded-l-[28px] sm:rounded-r-none sm:rounded-t-none sm:border-l sm:border-t-0" style={{ boxShadow: "var(--elev-4)" }} data-testid="global-bet-slip-mobile">
              {/* drag handle (36×4, surface-border) — §4.11 */}
              <div className="mx-auto mb-3 h-1 w-9 rounded-full bg-surface-border sm:hidden" />
              <SlipBody onClose={() => setOpenMobile(false)} />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      </>
      )}
    </>
  );
}
